/**
 * The one place the app talks to the server.
 *
 * Everything that is easy to get subtly wrong lives here rather than at each
 * call site: the base URL, credential mode, CSRF, JSON and error parsing,
 * timeouts, cancellation, and what happens when a session expires.
 *
 * Two rules this module exists to enforce:
 *
 *   **No token ever reaches browser storage.** Authentication is a `Secure`,
 *   `HttpOnly` session cookie the browser holds and JavaScript cannot read.
 *   There is no access token, no refresh token, and therefore nothing to put in
 *   `localStorage` or IndexedDB — which is the point, because anything readable
 *   from script is readable by an injected script.
 *
 *   **A failure is a value, not a surprise.** Every method resolves to a typed
 *   result or throws `ApiError` carrying a machine-readable `kind`. Callers
 *   switch on the kind; nobody parses an error message to decide what happened.
 */

import type { paths } from './schema';

/**
 * Where the API lives.
 *
 * Same-origin by default, which is what a same-origin proxy arrangement wants
 * and what every test uses. `VITE_API_ORIGIN` points the app at a separately
 * hosted Django service — it is a *public* value, the origin of a server anyone
 * can see, and it is the only backend-related variable that may ever appear in
 * a `VITE_*` name. Database URLs, Django's secret key, SMTP credentials and the
 * Turnstile secret are server-side configuration and would be readable by every
 * visitor if they were put here.
 */
export const API_ORIGIN = (import.meta.env?.VITE_API_ORIGIN ?? '').replace(/\/$/, '');

const DEFAULT_BASE = `${API_ORIGIN}/api/v1`;
const DEFAULT_TIMEOUT_MS = 15_000;

export type ApiErrorKind =
  | 'offline'
  | 'timeout'
  | 'cancelled'
  | 'unauthenticated'
  | 'forbidden'
  | 'not-found'
  | 'conflict'
  | 'validation'
  | 'rate-limited'
  | 'too-large'
  | 'server'
  | 'unknown';

export interface FieldError {
  field: string;
  message: string;
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  /** Per-field messages, when the server sent them. */
  readonly fields: FieldError[];
  readonly retryAfterSeconds?: number;

  constructor(
    kind: ApiErrorKind,
    message: string,
    options: { status?: number; fields?: FieldError[]; retryAfterSeconds?: number } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = options.status ?? 0;
    this.fields = options.fields ?? [];
    this.retryAfterSeconds = options.retryAfterSeconds;
  }

  /** True when retrying the identical request could plausibly succeed. */
  get retryable(): boolean {
    return this.kind === 'offline' || this.kind === 'timeout' || this.kind === 'server';
  }
}

function kindForStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not-found';
  if (status === 409) return 'conflict';
  if (status === 413) return 'too-large';
  if (status === 422 || status === 400) return 'validation';
  if (status === 429) return 'rate-limited';
  if (status >= 500) return 'server';
  return 'unknown';
}

/**
 * Turn whatever the server sent into field errors.
 *
 * Django Ninja emits `{detail: [{loc, msg}]}` for schema failures and
 * `{detail: "..."}` for `HttpError`; allauth headless emits `{errors: [{param,
 * message, code}]}`. All three are normalised here so no screen has to know
 * which layer rejected it.
 */
function parseFields(body: unknown): { message: string; fields: FieldError[] } {
  if (!body || typeof body !== 'object') return { message: '', fields: [] };
  const payload = body as Record<string, unknown>;

  if (Array.isArray(payload.errors)) {
    const fields = payload.errors
      .filter((e): e is Record<string, unknown> => Boolean(e) && typeof e === 'object')
      .map((e) => ({
        field: String(e.param ?? ''),
        message: String(e.message ?? ''),
      }));
    return { message: fields[0]?.message ?? '', fields };
  }

  const detail = payload.detail;
  if (typeof detail === 'string') return { message: detail, fields: [] };
  if (Array.isArray(detail)) {
    const fields = detail
      .filter((e): e is Record<string, unknown> => Boolean(e) && typeof e === 'object')
      .map((e) => ({
        // loc is like ["body", "payload", "title"]; the last element is the field.
        field: Array.isArray(e.loc) ? String(e.loc[e.loc.length - 1]) : '',
        message: String(e.msg ?? ''),
      }));
    return { message: fields[0]?.message ?? '', fields };
  }
  return { message: '', fields: [] };
}

export interface ApiClientOptions {
  baseUrl?: string;
  timeoutMs?: number;
  /**
   * Called when the server says the session is gone. The app uses it to move to
   * the signed-out state exactly once, rather than every in-flight request
   * separately deciding to redirect.
   */
  onSessionExpired?: () => void;
  fetchImpl?: typeof fetch;
}

export interface RequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Suppresses the session-expiry callback for probes that expect a 401. */
  expectUnauthenticated?: boolean;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;
  private readonly onSessionExpired?: () => void;
  private readonly fetchImpl: typeof fetch;
  /** Mirrors the CSRF cookie. Held in memory only; never persisted. */
  private csrfToken: string | null = null;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? DEFAULT_BASE).replace(/\/$/, '');
    this.timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.onSessionExpired = options.onSessionExpired;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  /**
   * Fetch the CSRF cookie. A JSON API never renders a form, so Django has no
   * other reason to issue one. Call once at startup, before any mutation.
   */
  async primeCsrf(options: RequestOptions = {}): Promise<void> {
    await this.request('GET', '/csrf', undefined, options);
    this.csrfToken = readCookie('mm_csrftoken');
  }

  private headers(method: string): Headers {
    const headers = new Headers({ Accept: 'application/json' });
    if (method !== 'GET' && method !== 'HEAD') {
      headers.set('Content-Type', 'application/json');
      // Read fresh each time: the cookie rotates on login, and a stale token
      // produces a 403 that looks like a permissions bug.
      const token = readCookie('mm_csrftoken') ?? this.csrfToken;
      if (token) headers.set('X-CSRFToken', token);
    }
    return headers;
  }

  async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    // The browser knows it is offline before a request is worth attempting.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      throw new ApiError('offline', 'You are offline.');
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(new DOMException('timeout', 'TimeoutError')), options.timeoutMs ?? this.timeoutMs);
    // A caller's own signal composes with the timeout rather than replacing it.
    const onAbort = () => controller.abort(options.signal?.reason);
    options.signal?.addEventListener('abort', onAbort, { once: true });

    let response: Response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method,
        headers: this.headers(method),
        // Cookies are the whole authentication mechanism; without this the
        // session simply is not sent.
        credentials: 'include',
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (error) {
      if (options.signal?.aborted) throw new ApiError('cancelled', 'Request cancelled.');
      if ((error as Error)?.name === 'TimeoutError' || controller.signal.aborted) {
        throw new ApiError('timeout', 'The server took too long to answer.');
      }
      throw new ApiError('offline', 'Could not reach the server.');
    } finally {
      clearTimeout(timeout);
      options.signal?.removeEventListener('abort', onAbort);
    }

    if (response.status === 204) return undefined as T;

    const text = await response.text();
    let payload: unknown = null;
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = null;
      }
    }

    if (response.ok) return payload as T;

    const { message, fields } = parseFields(payload);
    const kind = kindForStatus(response.status);

    if (kind === 'unauthenticated' && !options.expectUnauthenticated) {
      // One notification for the app, however many requests were in flight.
      this.onSessionExpired?.();
    }

    const retryAfter = Number(response.headers.get('Retry-After'));
    throw new ApiError(kind, message || defaultMessage(kind), {
      status: response.status,
      fields,
      retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
    });
  }

  get<T>(path: string, options?: RequestOptions) {
    return this.request<T>('GET', path, undefined, options);
  }
  post<T>(path: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>('POST', path, body, options);
  }
  patch<T>(path: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>('PATCH', path, body, options);
  }
  put<T>(path: string, body?: unknown, options?: RequestOptions) {
    return this.request<T>('PUT', path, body, options);
  }
  delete<T>(path: string, options?: RequestOptions) {
    return this.request<T>('DELETE', path, undefined, options);
  }
}

function defaultMessage(kind: ApiErrorKind): string {
  switch (kind) {
    case 'offline':
      return 'You are offline. Your work is still saved on this iPad.';
    case 'timeout':
      return 'The server took too long to answer.';
    case 'cancelled':
      return 'Request cancelled.';
    case 'unauthenticated':
      return 'Your session has ended. Sign in again to keep syncing.';
    case 'forbidden':
      return 'You do not have access to that.';
    case 'not-found':
      return 'Not found.';
    case 'conflict':
      return 'That has changed since you loaded it.';
    case 'validation':
      return 'Check the highlighted fields.';
    case 'rate-limited':
      return 'Too many attempts. Wait a moment and try again.';
    case 'too-large':
      return 'That is too large to send.';
    case 'server':
      return 'Something went wrong on our side.';
    default:
      return 'Something went wrong.';
  }
}

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[2]) : null;
}

// -------------------------------------------------------- typed convenience

/** Response body of a GET, taken straight from the generated contract. */
type GetBody<P extends keyof paths> = paths[P] extends {
  get: { responses: { 200: { content: { 'application/json': infer R } } } };
}
  ? R
  : never;

export type Profile = GetBody<'/api/v1/me'>;
export type DocumentSummary = GetBody<'/api/v1/documents'>[number];
export type LessonProgressOut = GetBody<'/api/v1/progress'>[number];
